import { ecId, ecLean, HandBack, type JanusCtx, type Row } from '../ability/context';
import { emptySpawnPlan, mergeSpawnPlans } from '../spawn/row-plan';
import { deathStatusRows } from './cleanup';
import { deathHolders } from './departure';
import { planRecursiveDelete } from '../inventory/delete-plan';
import { readInventoryField } from '../compile/inventory-raw-readers';
import { deathLane, deathResourcesFor } from './compose';

/** Deletion uses the death ledger's exact caster/connection/concentration outcomes.
 * Roster deletion grants no death Gift. Attachment tombstones are fixed storage cleanup. A living
 * departure preserves vitality and casts on other holders, while its own rows leave. */
export function composeCreatureDeletion22(ctx: JanusCtx, target: Row, action: 'delete' | 'destroy' | 'flee' = 'delete'): void {
    const id = ecId(target);
    if (ctx.composedDeaths?.includes(id)) return;
    const rowStart = ctx.rows.length;
    const plan = emptySpawnPlan();
    const physical = deathStatusRows(deathHolders([ctx.encounter, ...ctx.members.values()]));
    const resources = deathResourcesFor(ctx, target);
    deathLane(ctx, resources, plan, undefined, { transportHandled: true }).depart(target, {
        departedAlive: action === 'flee', rosterDelete: action === 'delete', storageCleanup: true,
    });
    const creatures = [id, ...plan.writes.filter(write => write.table === 'encounter_creatures'
        && Number(ecLean(write.row, 'encounter_creature_deleted') ?? 0) === 1).map(write => ecId(write.row))];
    const deleted = [...creatures, ...plan.writes.filter(write => write.table === 'inventory'
        && Number(readInventoryField(write.row, 'inventory_deleted') ?? 0) === 1).map(write => String(readInventoryField(write.row, 'inventory_id')))];
    planRecursiveDelete(ctx, deleted, action === 'flee' ? [] : creatures, physical,
        resources.casterDeathStatusIds.map(item_id => ({ item_id, ends_on_caster_death: true })));
    if (ctx.rows.slice(rowStart).some(row => row.roll_type_1 === 'janus_unported_skip'))
        throw new HandBack('creature-delete:follow-up-not-ported');
    for (const status of ctx.statusesAdded) status.status_id ??= ctx.read.uuid();
    ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
    (ctx.composedDeaths ??= []).push(id);
}

/** The encounter editor's delete also closes creature-number gaps, in saved number order.
 * Lair removal and catalog-PC deletion never run this authoring duty. */
export function renumberCreatureDeletion22(ctx: JanusCtx): void {
    const live = [...ctx.members.values()].filter(row => ecLean(row, 'item_type') === 'creature'
        && Number(ecLean(row, 'encounter_creature_deleted') ?? 0) === 0)
        .sort((a, b) => Number(ecLean(a, 'creature_number') ?? 0) - Number(ecLean(b, 'creature_number') ?? 0));
    const plan = emptySpawnPlan();
    live.forEach((row, index) => {
        const before = Number(ecLean(row, 'creature_number') ?? 0), number = index + 1;
        if (before !== number) plan.writes.push({ table: 'encounter_creatures', op: 'update',
            row: { encounter_creature_id: ecId(row), creature_number: number }, before: { creature_number: before } });
    });
    ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
}
